import { SVGProps } from 'react';

import { WidgetId } from '../../../types/idTypes';
import { AccessibleElement, Widget } from '../../../types/widget.types';
import { ArrowStyleTypeKeys, LineWidgetDashArrayTypeKeys } from '../common/CommonLine.types';

export enum LineWidgetTypes {
  straight = 'straight',
  stepped = 'stepped',
  curved = 'curved',
}

/**
 * PortPosition Component
 */
export enum Alignment {
  BEGIN = 'BEGIN',
  CENTER = 'CENTER',
  END = 'END',
}

export enum Side {
  CENTER = 'CENTER',
  EAST = 'EAST',
  WEST = 'WEST',
  SOUTH = 'SOUTH',
  NORTH = 'NORTH',
}

export type LineWidgetTypeKeys = keyof typeof LineWidgetTypes;

/**
 * Extra data which needs to be saved to backend
 */
export interface Pos {
  xPx: number;
  yPx: number;
}

export interface DeltaPos extends Pos {
  targetIndex?: number;
}

export interface AnglePos extends Pos {
  angle: number;
  distance: number;
}

// For connection lines
export interface PortData {
  widgetId: WidgetId;
  alignment: Alignment;
  side: Side;
  offsetPx?: number; // When using a detailed offset to the port location
  offsetDeg?: number; // for center
}

interface LineConnection {
  startPort: PortData | null;
  endPort: PortData | null;
}

export interface PointGuidesForSteps {
  gridPoints: Pos[];
  normalPoints: Pos[];
  points: Pos[];
}

export type Axis = 'x' | 'y';

// For infinite lines, e.x: y = 4 or x = 2
export interface CartesianLine {
  axis: Axis;
  value: number;
}

export interface Line {
  start: Pos;
  end: Pos;
}
export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
  rotateDeg?: number;
}

export interface LineWidgetData extends Widget, AccessibleElement, LineConnection {
  type: LineWidgetTypes;
  startPos: Pos;
  midPosList: Pos[];
  endPos: Pos;
  strokeWidth: number;
  strokeColor: string;
  strokeDashType: LineWidgetDashArrayTypeKeys;
  startArrowStyle: ArrowStyleTypeKeys;
  endArrowStyle: ArrowStyleTypeKeys;
}

export interface LineRenderProps {
  newPosList: Pos[];
  widthPx: number;
  heightPx: number;
  strokeWidth: number;
  startArrowStyle: ArrowStyleTypeKeys;
  endArrowStyle: ArrowStyleTypeKeys;
  fitSvgToPathSize?: boolean;
}

export type LineWidgetStyle = Pick<
  LineWidgetData,
  'strokeWidth' | 'strokeColor' | 'strokeDashType' | 'startArrowStyle' | 'endArrowStyle' | 'type'
>;

export interface CommonLineProps {
  id?: string;
  widthPx?: number;
  heightPx?: number;
  posList: Pos[];
  strokeWidth: number;
  strokeColor: string;
  strokeDashType: LineWidgetDashArrayTypeKeys;
  startArrowStyle: ArrowStyleTypeKeys;
  endArrowStyle: ArrowStyleTypeKeys;
  delta?: DeltaPos;
  isReadonly?: boolean;
  startPort?: PortData | null;
  endPort?: PortData | null;
  topPx?: number;
  leftPx?: number;
  realTimeStartRect?: Rect | null;
  realTimeEndRect?: Rect | null;
  realTimeStartPort?: PortData | null;
  realTimeEndPort?: PortData | null;
  isUseRealTimeStartRect?: boolean;
  isUseRealTimeEndRect?: boolean;
  fitSvgToPathSize?: boolean;
}

export interface SteppedLineProps extends CommonLineProps {
  startSide?: Side;
  endSide?: Side;
  startRect?: Rect;
  endRect?: Rect;
}

export interface StraightLineProps extends CommonLineProps {}

export type LineProps = SteppedLineProps | StraightLineProps;

export interface DragHandlerProps extends LineConnection {
  widgetId?: string;
  leftPx: number;
  topPx: number;
  startPort: PortData | null;
  endPort: PortData | null;
  posList: Pos[];
  zoom: number;
  handlerSize: number;
  activeHandlerIndex: number;
  setFocus: Function;
  delta: DeltaPos;
  setDelta: Function;
  updateData: Function;
  setRealTimeStartRect?: Function;
  setRealTimeEndRect?: Function;
  setRealTimeStartPort?: Function;
  setRealTimeEndPort?: Function;
  setIsUseRealTimeStartRect?: Function;
  setIsUseRealTimeEndRect?: Function;
}

export enum WidgetState {
  Default = 'default',
  Active = 'active',
  Edit = 'edit',
}
export interface LineWidgetControlKeyboardShortcutsProps {
  setWidgetState: (arg: WidgetState) => void;
  widgetState: WidgetState;
  setFocus: Function;
  onKeyEvent: Function;
}
export interface SetFocusProps {
  moveNext?: boolean;
  movePrev?: boolean;
  index?: number;
  isWidget?: boolean;
}

/**
 * Config to be passed to <marker> and <path> elements for different line end styles
 */
export type LineEndConfig = {
  markerProps: Partial<SVGProps<SVGMarkerElement>>;
  pathProps: Partial<SVGProps<SVGPathElement>>;
  strokeMultiplier?: number;
};
